import React, { useEffect, useState } from "react";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { api } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

const L = {
  fr: { title: "Comptes pro", sub: "Créez et gérez les comptes des professionnels", add: "Nouveau compte pro",
    email: "Email de connexion", pw: "Mot de passe", company: "Nom de l'entreprise", slug: "Lien public (identifiant)",
    country: "Pays du professionnel", iban: "IBAN (compte pour acomptes)", create: "Créer le compte", link: "Lien public", copy: "Copier", copied: "Copié ✓", quotes: "devis", upload_logo: "Logo", logo_saved: "Logo mis à jour ✓",
    active: "Actif", inactive: "Désactivé", reset_pw: "Réinitialiser le mot de passe", new_pw: "Nouveau mot de passe (min. 6)",
    del: "Supprimer", del_confirm: "Supprimer définitivement ce compte pro ?", save: "Enregistrer", cancel: "Annuler",
    superadmin: "Super-admin", empty: "Aucun compte pro pour l'instant.", saved: "Enregistré ✓",
    email_sent: "Compte créé — email de connexion envoyé au pro ✓", email_fail: "Compte créé ✓ (l'email n'a pas pu être envoyé — communiquez les identifiants ci-dessous au pro)",
    creds_title: "Identifiants du compte créé", creds_hint: "Copiez et transmettez ces informations au professionnel (l'email automatique n'est pas encore actif).", pw_reset_done: "Mot de passe réinitialisé. Nouveau mot de passe :", close: "Fermer",
    s_active: "Abonnement actif", s_trial: "Essai", s_expired: "Abonnement expiré", s_none: "Aucun abonnement", renews: "renouvellement",
    dup: "Dupliquer la grille tarifaire", dup_title: "Dupliquer la grille tarifaire", dup_from: "Copier la grille de", dup_to: "Vers ces professionnels :", dup_confirm: "Dupliquer", dup_none: "Aucun autre pro disponible.", dup_done: "Grille dupliquée vers {n} pro(s) ✓", dup_warn: "Cela écrasera la grille tarifaire (et les fournitures perso) des pros sélectionnés." },
  nl: { title: "Pro-accounts", sub: "Maak en beheer de accounts van professionals", add: "Nieuw pro-account",
    email: "Login e-mail", pw: "Wachtwoord", company: "Bedrijfsnaam", slug: "Openbare link (id)",
    country: "Land van de professional", iban: "IBAN (rekening voor voorschotten)", create: "Account aanmaken", link: "Openbare link", copy: "Kopiëren", copied: "Gekopieerd ✓", quotes: "offertes", upload_logo: "Logo", logo_saved: "Logo bijgewerkt ✓",
    active: "Actief", inactive: "Uitgeschakeld", reset_pw: "Wachtwoord resetten", new_pw: "Nieuw wachtwoord (min. 6)",
    del: "Verwijderen", del_confirm: "Dit pro-account definitief verwijderen?", save: "Opslaan", cancel: "Annuleren",
    superadmin: "Super-admin", empty: "Nog geen pro-accounts.", saved: "Opgeslagen ✓",
    email_sent: "Account aangemaakt — login-e-mail verzonden ✓", email_fail: "Account aangemaakt ✓ (e-mail kon niet worden verzonden — deel de gegevens hieronder met de pro)",
    creds_title: "Inloggegevens van het aangemaakte account", creds_hint: "Kopieer en deel deze gegevens met de professional (automatische e-mail is nog niet actief).", pw_reset_done: "Wachtwoord gereset. Nieuw wachtwoord:", close: "Sluiten",
    s_active: "Actief abonnement", s_trial: "Proef", s_expired: "Abonnement verlopen", s_none: "Geen abonnement", renews: "verlenging",
    dup: "Prijstabel dupliceren", dup_title: "Prijstabel dupliceren", dup_from: "Tabel kopiëren van", dup_to: "Naar deze professionals:", dup_confirm: "Dupliceren", dup_none: "Geen andere pro beschikbaar.", dup_done: "Tabel gedupliceerd naar {n} pro(s) ✓", dup_warn: "Dit overschrijft de prijstabel (en eigen leveringen) van de geselecteerde pro's." },
  en: { title: "Pro accounts", sub: "Create and manage professional accounts", add: "New pro account",
    email: "Login email", pw: "Password", company: "Company name", slug: "Public link (slug)",
    country: "Professional's country", iban: "IBAN (deposit account)", create: "Create account", link: "Public link", copy: "Copy", copied: "Copied ✓", quotes: "quotes", upload_logo: "Logo", logo_saved: "Logo updated ✓",
    active: "Active", inactive: "Disabled", reset_pw: "Reset password", new_pw: "New password (min. 6)",
    del: "Delete", del_confirm: "Permanently delete this pro account?", save: "Save", cancel: "Cancel",
    superadmin: "Super-admin", empty: "No pro accounts yet.", saved: "Saved ✓",
    email_sent: "Account created — login email sent ✓", email_fail: "Account created ✓ (email could not be sent — share the credentials below with the pro)",
    creds_title: "Created account credentials", creds_hint: "Copy and share these details with the professional (automatic email is not active yet).", pw_reset_done: "Password reset. New password:", close: "Close",
    s_active: "Active subscription", s_trial: "Trial", s_expired: "Subscription expired", s_none: "No subscription", renews: "renewal",
    dup: "Duplicate pricing grid", dup_title: "Duplicate pricing grid", dup_from: "Copy grid from", dup_to: "To these professionals:", dup_confirm: "Duplicate", dup_none: "No other pro available.", dup_done: "Grid duplicated to {n} pro(s) ✓", dup_warn: "This will overwrite the pricing grid (and custom supplies) of the selected pros." },
  de: { title: "Pro-Konten", sub: "Konten der Fachleute erstellen und verwalten", add: "Neues Pro-Konto",
    email: "Login-E-Mail", pw: "Passwort", company: "Firmenname", slug: "Öffentlicher Link (Kennung)",
    country: "Land des Fachmanns", iban: "IBAN (Konto für Anzahlungen)", create: "Konto erstellen", link: "Öffentlicher Link", copy: "Kopieren", copied: "Kopiert ✓", quotes: "Angebote", upload_logo: "Logo", logo_saved: "Logo aktualisiert ✓",
    active: "Aktiv", inactive: "Deaktiviert", reset_pw: "Passwort zurücksetzen", new_pw: "Neues Passwort (min. 6)",
    del: "Löschen", del_confirm: "Dieses Pro-Konto endgültig löschen?", save: "Speichern", cancel: "Abbrechen",
    superadmin: "Super-admin", empty: "Noch keine Pro-Konten.", saved: "Gespeichert ✓",
    email_sent: "Konto erstellt — Login-E-Mail gesendet ✓", email_fail: "Konto erstellt ✓ (E-Mail konnte nicht gesendet werden — geben Sie die Zugangsdaten unten an den Pro weiter)",
    creds_title: "Zugangsdaten des erstellten Kontos", creds_hint: "Kopieren Sie diese Angaben und geben Sie sie an den Fachmann weiter (automatische E-Mail noch nicht aktiv).", pw_reset_done: "Passwort zurückgesetzt. Neues Passwort:", close: "Schließen",
    s_active: "Aktives Abo", s_trial: "Test", s_expired: "Abo abgelaufen", s_none: "Kein Abo", renews: "Verlängerung",
    dup: "Preistabelle duplizieren", dup_title: "Preistabelle duplizieren", dup_from: "Tabelle kopieren von", dup_to: "Zu diesen Fachleuten:", dup_confirm: "Duplizieren", dup_none: "Kein anderer Pro verfügbar.", dup_done: "Tabelle zu {n} Pro(s) dupliziert ✓", dup_warn: "Dies überschreibt die Preistabelle (und eigene Lieferungen) der ausgewählten Pros." },
};

const COUNTRIES = [
  { code: "BE", label: "Belgique" },
  { code: "FR", label: "France" },
  { code: "LU", label: "Luxembourg" },
  { code: "NL", label: "Pays-Bas" },
  { code: "DE", label: "Allemagne" },
];

const SUB_STYLE = {
  active: "bg-[#EAF0EB] text-ok",
  trial: "bg-[#EDE7F3] text-[#6A4FA3]",
  expired: "bg-[#F3E7E6] text-err",
  none: "bg-paper-3 text-ink-3",
};

export default function Users() {
  const { lang } = useI18n();
  const tt = L[lang] || L.fr;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", company_name: "", slug: "", country: "BE", iban: "" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");
  const [notice, setNotice] = useState(null);
  const [created, setCreated] = useState(null);
  const [dupFrom, setDupFrom] = useState(null);
  const [dupTargets, setDupTargets] = useState([]);
  const [duplicating, setDuplicating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listUsers(); setUsers(d.users); setError(""); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true); setError(""); setNotice(null); setCreated(null);
    try {
      const r = await api.createUser(form);
      const cred = {
        email: form.email.trim(), password: form.password.trim(),
        slug: r.user?.slug, email_sent: r.email_sent,
      };
      setForm({ email: "", password: "", company_name: "", slug: "", country: "BE", iban: "" });
      setShowCreate(false);
      setCreated(cred);
      setNotice({ ok: r.email_sent, text: r.email_sent ? tt.email_sent : tt.email_fail });
      await load();
    } catch (e) { setError(e.message); } finally { setCreating(false); }
  };

  const toggleActive = async (u) => {
    await api.updateUser(u.id, { active: !u.active });
    load();
  };
  const resetPw = async (u) => {
    const pw = window.prompt(tt.new_pw);
    if (!pw) return;
    try {
      await api.updateUser(u.id, { password: pw });
      setNotice({ ok: true, text: `${tt.pw_reset_done} ${pw.trim()} (${u.email})` });
    } catch (e) { setError(e.message); }
  };
  const del = async (u) => {
    if (!window.confirm(tt.del_confirm)) return;
    try { await api.deleteUser(u.id); load(); } catch (e) { setError(e.message); }
  };
  const uploadLogo = async (u, file) => {
    setError(""); setNotice(null);
    try { await api.uploadLogoFor(u.id, file); setNotice({ ok: true, text: tt.logo_saved }); await load(); }
    catch (e) { setError(e.message); }
  };
  const publicUrl = (slug) => `${window.location.origin}/devis/${slug}`;
  const copy = (slug) => {
    navigator.clipboard?.writeText(publicUrl(slug));
    setCopied(slug); setTimeout(() => setCopied(""), 1800);
  };

  const pros = users.filter((u) => u.role !== "superadmin");
  const openDup = (u) => { setDupFrom(u); setDupTargets([]); setNotice(null); setError(""); };
  const toggleTarget = (id) => setDupTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const runDuplicate = async () => {
    if (!dupFrom || dupTargets.length === 0) return;
    setDuplicating(true); setError("");
    try {
      const r = await api.duplicatePricing(dupFrom.id, dupTargets);
      setNotice({ ok: true, text: tt.dup_done.replace("{n}", r.copied) });
      setDupFrom(null); setDupTargets([]);
    } catch (e) { setError(e.message); } finally { setDuplicating(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-semibold text-ink">{tt.title}</h1>
          <p className="mt-1 text-[14px] text-mute">{tt.sub}</p>
        </div>
        <button data-testid="user-add-toggle" onClick={() => setShowCreate((s) => !s)} className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-[14px] font-semibold text-white">
          <Icon name={showCreate ? "x" : "plus"} size={17} className="text-white" /> {tt.add}
        </button>
      </div>

      {showCreate && (
        <div className="mb-6 rounded-2xl border border-line bg-white p-5">
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field label={tt.company} testId="user-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Peinture Martin" />
            <Field label={tt.slug} testId="user-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="martin" />
            <Field label={tt.iban} testId="user-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="BE68 5390 0754 7034" />
            <Field label={tt.email} testId="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="pro@email.be" />
            <Field label={tt.pw} testId="user-pw" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            <label className="mb-4 block">
              <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.country}</span>
              <select data-testid="user-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="field-input">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-2 max-w-xs">
            <PrimaryButton testId="user-create-btn" title={tt.create} icon="check" onClick={create} loading={creating} disabled={!form.email || form.password.length < 6} />
          </div>
        </div>
      )}

      {error ? <p data-testid="users-error" className="mb-4 text-[14px] font-medium text-err">{error}</p> : null}
      {notice ? <p data-testid="users-notice" className={`mb-4 text-[14px] font-medium ${notice.ok ? "text-ok" : "text-warn"}`}>{notice.text}</p> : null}

      {created ? (
        <div data-testid="created-credentials" className="mb-6 rounded-2xl border-[1.5px] border-brand bg-brand-tertiary p-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-brand-on">
              <Icon name="key" size={16} className="text-brand" /> {tt.creds_title}
            </h3>
            <button data-testid="creds-close" onClick={() => setCreated(null)} className="text-[13px] font-medium text-brand-on">{tt.close}</button>
          </div>
          <p className="mb-3 text-[12px] text-ink-3">{tt.creds_hint}</p>
          <div className="space-y-1 rounded-xl bg-white p-3 text-[14px] text-ink">
            <p><span className="text-mute">{tt.email} :</span> <b>{created.email}</b></p>
            <p><span className="text-mute">{tt.pw} :</span> <b>{created.password}</b></p>
            {created.slug ? <p className="break-all"><span className="text-mute">{tt.link} :</span> <b>{`${window.location.origin}/devis/${created.slug}`}</b></p> : null}
          </div>
          <button
            data-testid="creds-copy"
            onClick={() => { navigator.clipboard?.writeText(`${tt.email}: ${created.email}\n${tt.pw}: ${created.password}\n${tt.link}: ${window.location.origin}/devis/${created.slug}`); setCopied("creds"); setTimeout(() => setCopied(""), 1800); }}
            className="mt-3 flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-[13px] font-semibold text-white"
          >
            <Icon name="copy" size={14} className="text-white" /> {copied === "creds" ? tt.copied : tt.copy}
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : users.length === 0 ? (
        <p className="text-[14px] text-mute">{tt.empty}</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="rounded-2xl border border-line bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[16px] font-semibold text-ink">{u.company?.company_name || u.email}</p>
                    {u.role === "superadmin" ? (
                      <span className="rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold text-white">{tt.superadmin}</span>
                    ) : (
                      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", u.active ? "bg-[#EAF0EB] text-ok" : "bg-[#F3E7E6] text-err")}>
                        {u.active ? tt.active : tt.inactive}
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-mute">{u.email} · {u.quote_count} {tt.quotes}{u.country ? ` · ${(COUNTRIES.find((c) => c.code === u.country) || {}).label || u.country}` : ""}</p>
                  {u.role !== "superadmin" ? (
                    <span data-testid={`user-sub-${u.id}`} className={cn("mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", SUB_STYLE[u.sub_status] || SUB_STYLE.none)}>
                      <Icon name={u.sub_status === "expired" ? "alert-circle" : u.sub_status === "none" ? "minus-circle" : "check-circle"} size={11} />
                      {tt[`s_${u.sub_status}`] || tt.s_none}
                      {u.subscription?.end_date && !u.subscription?.trial ? ` · ${tt.renews} ${String(u.subscription.end_date).slice(0, 10)}` : ""}
                    </span>
                  ) : null}
                  {u.slug ? (
                    <button data-testid={`user-copy-${u.id}`} onClick={() => copy(u.slug)} className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-brand">
                      <Icon name="link" size={13} /> {publicUrl(u.slug)}
                      <span className="ml-1 text-mute">{copied === u.slug ? tt.copied : `· ${tt.copy}`}</span>
                    </button>
                  ) : null}
                </div>
                {u.role !== "superadmin" && (
                  <div className="flex items-center gap-2">
                    <label data-testid={`user-logo-${u.id}`} title={tt.upload_logo} className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-line px-2">
                      {u.company?.logo_url ? (
                        <img src={u.company.logo_url} alt="" className="h-6 max-w-[44px] object-contain" />
                      ) : (
                        <Icon name="image" size={16} className="text-ink-3" />
                      )}
                      <Icon name="upload" size={14} className="text-ink-3" />
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(u, f); e.target.value = ""; }} />
                    </label>
                    <button data-testid={`user-dup-${u.id}`} onClick={() => openDup(u)} title={tt.dup} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line">
                      <Icon name="copy" size={16} className="text-brand" />
                    </button>
                    <button data-testid={`user-toggle-${u.id}`} onClick={() => toggleActive(u)} title={u.active ? tt.inactive : tt.active} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line">
                      <Icon name={u.active ? "pause" : "play"} size={16} className="text-ink-3" />
                    </button>
                    <button data-testid={`user-resetpw-${u.id}`} onClick={() => resetPw(u)} title={tt.reset_pw} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line">
                      <Icon name="key" size={16} className="text-ink-3" />
                    </button>
                    <button data-testid={`user-del-${u.id}`} onClick={() => del(u)} title={tt.del} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line">
                      <Icon name="trash-2" size={16} className="text-err" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {dupFrom && (
        <div data-testid="dup-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDupFrom(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[16px] font-semibold text-ink">
                <Icon name="copy" size={16} className="text-brand" /> {tt.dup_title}
              </h3>
              <button data-testid="dup-close" onClick={() => setDupFrom(null)} className="text-ink-3"><Icon name="x" size={18} /></button>
            </div>
            <p className="mb-2 text-[13px] text-mute">{tt.dup_from} <b className="text-ink">{dupFrom.company?.company_name || dupFrom.email}</b></p>
            <p className="mb-3 text-[13px] font-medium text-ink-3">{tt.dup_to}</p>
            {pros.filter((p) => p.id !== dupFrom.id).length === 0 ? (
              <p className="text-[13px] text-mute">{tt.dup_none}</p>
            ) : (
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {pros.filter((p) => p.id !== dupFrom.id).map((p) => (
                  <label key={p.id} data-testid={`dup-target-${p.id}`} className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-line px-3 py-2.5">
                    <input type="checkbox" checked={dupTargets.includes(p.id)} onChange={() => toggleTarget(p.id)} className="h-4 w-4 accent-[var(--brand)]" />
                    <span className="text-[14px] text-ink">{p.company?.company_name || p.email}</span>
                    <span className="ml-auto text-[12px] text-mute">{p.email}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="mt-3 text-[12px] text-warn">{tt.dup_warn}</p>
            <div className="mt-4">
              <PrimaryButton testId="dup-confirm" title={tt.dup_confirm} icon="copy" onClick={runDuplicate} loading={duplicating} disabled={dupTargets.length === 0} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
